import { CommonModule, NgClass, NgForOf, NgIf } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

interface Video {
  id: number;
  title: string;
  thumbnail: string;
  videoUrl: string;
  safeUrl?: SafeResourceUrl;
  thumbnailError?: boolean;
}

@Component({
  selector: 'app-gallery',
  standalone: true,
  imports: [CommonModule, NgForOf, NgIf, NgClass],
  templateUrl: './gallery.component.html',
  styleUrls: ['./gallery.component.scss'],
})
export class GalleryComponent implements OnInit {
  videos: Video[] = [
    { id: 1, title: `Client Visit`, thumbnail: '../../../assets/home/gallery/clientvisit.png', videoUrl: 'https://youtu.be/W84abe2ciTE?si=U3zyZpud62M9rtzp' },
    { id: 2, title: `Reliving the Best of 2024`, thumbnail: '../../../assets/home/gallery/RewindReliving_the_Best_of_2024.png', videoUrl: 'https://youtu.be/XCTHZzU4SSk?si=3s0-HDtmk9qUWzfy' },
    { id: 3, title: 'OP Client Visit', thumbnail: '../../../assets/home/gallery/op-client-visit.png', videoUrl: 'https://youtube.com/shorts/fgWb2pAVQPU?feature=share' },
    { id: 4, title: `Diwali 2025!`, thumbnail: '../../../assets/home/gallery/diwali.png', videoUrl: 'https://youtube.com/shorts/ZGruhhAeHu8?si=WR69TwuligU5gp6a' },
    { id: 5, title: `SPARK 2024!`, thumbnail: '../../../assets/home/gallery/spark.png', videoUrl: 'https://youtu.be/7kfUTOdvimw?si=ecidxcx3Kwni74jJ' },
    { id: 6, title: `Office Fun`, thumbnail: '../../../assets/home/gallery/office_fun.png', videoUrl: 'https://youtube.com/shorts/m1focDvX2dI?si=oZEIJ1QHxcrEc1rH' },
    { id: 7, title: `BTS: Monthly Town Hall`, thumbnail: '../../../assets/home/gallery/BTS_MonthlyTownHall.png', videoUrl: 'https://youtu.be/rTrtzYlJ5m4?si=Fs3CCuj_smRFfV7P' },
    { id: 8, title: `7th Anniversary Celebration`, thumbnail: '../../../assets/home/gallery/7thanniversary.png', videoUrl: 'https://youtu.be/Ml0K_FKguI4?si=PilLfEPiHB7hNLrj' },
    { id: 9, title: `Azure Cognitive Search`, thumbnail: '../../../assets/home/gallery/azure_cognitive_search.png', videoUrl: 'https://youtu.be/DsQTBye3TGI?si=p6at8N_OL5VJMmqR' },
    { id: 10, title: `Virtual Office Tour`, thumbnail: '../../../assets/home/gallery/virtual_office_tour.png', videoUrl: 'https://youtu.be/zTDy0Yg-4SQ?si=Juf_WTNKA7ZHtqPh' },
    { id: 11, title: `Leader's Voice with Kube`, thumbnail: '../../../assets/home/gallery/leaderceovoice.png', videoUrl: 'https://youtu.be/QMGC7fViFn0?si=AcPfz56vuqVE7uZp' },
  ];

  // Start at 0 (Video 1)
  currentCenterIndex = 0;

  // The subset of videos currently visible
  currentSlideVideos: Video[] = [];

  dotCount = 0;
  showVideoModal: boolean = false;
  currentModalVideo: Video | null = null;

  itemsPerSlide = 5;
  centerOffset = 2; // (5 items / 2 rounded down)

  constructor(private sanitizer: DomSanitizer) { }

  ngOnInit(): void {
    this.checkScreenSize();
    this.sanitizeVideoUrls();
    this.calculateDots();
    this.updateCurrentSlide();
  }

  // REQUIRED for smooth transitions: 
  // Tells Angular to reuse the DOM element if the ID matches, 
  // preventing the "flicker" of destruction/recreation.
  trackByVideoId(index: number, video: Video): number {
    return video.id;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkScreenSize();
  }

  checkScreenSize() {
    const width = window.innerWidth;
    if (width < 992) {
      if (this.itemsPerSlide !== 3) {
        this.itemsPerSlide = 3;
        this.centerOffset = 1;
        this.calculateDots();
        this.updateCurrentSlide();
      }
    } else {
      if (this.itemsPerSlide !== 5) {
        this.itemsPerSlide = 5;
        this.centerOffset = 2;
        this.calculateDots();
        this.updateCurrentSlide();
      }
    }
  }

  calculateDots() {
    this.dotCount = Math.ceil(this.videos.length / this.itemsPerSlide);
  }

  sanitizeVideoUrls(): void {
    this.videos.forEach(v => {
      if (this.isYouTube(v.videoUrl)) {
        v.safeUrl = this.getEmbedUrl(v.videoUrl);
      }
    });
  }

  // Calculates the 5 videos to show based on the currentCenterIndex
  updateCurrentSlide(): void {
    const len = this.videos.length;
    this.currentSlideVideos = [];

    // We want the currentCenterIndex to be in the MIDDLE of the array
    for (let i = -this.centerOffset; i <= this.centerOffset; i++) {
      // Modulo math to handle wrapping (e.g., index -1 becomes index 9)
      const idx = (this.currentCenterIndex + i + len) % len;
      this.currentSlideVideos.push(this.videos[idx]);
    }
  }

  onThumbnailClick(idx: number): void {
    const centerPosition = this.centerOffset; // e.g. 2 for Desktop

    if (idx === centerPosition) {
      // 1. Clicked the CENTER video -> PLAY
      this.currentModalVideo = this.currentSlideVideos[idx];
      this.showVideoModal = true;
      document.body.style.overflow = 'hidden';
    } else {
      // 2. Clicked a SIDE video -> ANIMATE TO CENTER
      // Calculate how far we need to move
      const offset = idx - centerPosition;

      // Update the main index
      this.currentCenterIndex = (this.currentCenterIndex + offset + this.videos.length) % this.videos.length;

      // Update the array (Angular trackBy will handle the smooth move)
      this.updateCurrentSlide();
    }
  }

  closeVideoModal(): void {
    this.showVideoModal = false;
    this.currentModalVideo = null;
    document.body.style.overflow = 'auto';
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.showVideoModal) {
      this.closeVideoModal();
    }
  }

  isYouTube(url: string): boolean {
    return url.includes('youtube.com') || url.includes('youtu.be');
  }

  getEmbedUrl(url: string): SafeResourceUrl {
    let videoId = '';
    if (url.includes('shorts/')) {
      videoId = url.split('shorts/')[1].split('?')[0];
    } else if (url.includes('watch?v=')) {
      videoId = url.split('watch?v=')[1].split('?')[0];
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1].split('?')[0];
    }
    const embed = videoId
      ? `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&enablejsapi=1&origin=${window.location.origin}`
      : url;
    return this.sanitizer.bypassSecurityTrustResourceUrl(embed);
  }

  getVideoSizeClass(idx: number): string {
    const center = this.centerOffset;
    if (idx === center) return 'video-large active';
    if (this.itemsPerSlide === 5) {
      if (idx === 1 || idx === 3) return 'video-medium';
    }
    if (this.itemsPerSlide === 3) {
      return 'video-medium';
    }
    return 'video-small';
  }

  getActiveDot(): number {
    return Math.floor(this.currentCenterIndex / this.itemsPerSlide);
  }

  onDotClick(dotIndex: number) {
    const targetIndex = (dotIndex * this.itemsPerSlide) % this.videos.length;
    this.currentCenterIndex = targetIndex;
    this.updateCurrentSlide();
  }
}